// Two small charts for the free report card: cumulative net P&L trade by trade (trades taken
// after 2 losses that day marked), and net P&L by hour of the day.
import { useState } from 'react';
import { formatMoney, netPnl, tradingDayKey } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const W = 640;
const H = 200;
const PAD = 14;

export function EquityChart({ trades }) {
  const { t } = useI18n();
  const [hover, setHover] = useState(null);
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  if (sorted.length < 3) return null;
  let running = 0;
  const lossesToday = {};
  const points = sorted.map((tr) => {
    const day = tradingDayKey(tr.date);
    const net = netPnl(tr);
    const afterTwo = (lossesToday[day] || 0) >= 2;
    if (net < 0) lossesToday[day] = (lossesToday[day] || 0) + 1;
    running += net;
    return { value: running, net, afterTwo, date: tr.date, symbol: tr.symbol };
  });
  const values = [0, ...points.map((p) => p.value)];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i) => PAD + (i / (points.length - 1)) * (W - PAD * 2);
  const y = (v) => PAD + (1 - (v - min) / span) * (H - PAD * 2);
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const flagged = points.filter((p) => p.afterTwo);
  const last = points[points.length - 1];
  const h = hover === null ? null : points[hover];
  const when = (d) => new Date(d).toLocaleString(currentLocale(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <section className="insight-panel report-chart">
      <h2>{t('Your P&L, trade by trade')}</h2>
      <p className="admin-panel-sub">
        {flagged.length
          ? t('Red dots are trades taken after 2 losses that day: {n} trades, {amount}.', { n: flagged.length, amount: formatMoney(flagged.reduce((s, p) => s + p.net, 0)) })
          : t('Cumulative net P&L after fees, in the order you traded.')}
      </p>
      <div className="report-chart-box"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setHover(Math.max(0, Math.min(points.length - 1, Math.round(((e.clientX - r.left) / r.width) * (points.length - 1)))));
        }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
          aria-label={t('Cumulative net P&L over {n} trades, ending at {amount}', { n: points.length, amount: formatMoney(last.value) })}>
          <line x1={PAD} x2={W - PAD} y1={y(0)} y2={y(0)} className="compare-zero" />
          <path d={path} className="report-chart-line" />
          {points.map((p, i) => p.afterTwo && <circle key={i} cx={x(i)} cy={y(p.value)} r="4" className="report-chart-flag" />)}
          {h && <line x1={x(hover)} x2={x(hover)} y1={PAD} y2={H - PAD} className="compare-cross" />}
        </svg>
        {h && (
          <div className={`compare-tip${hover > points.length / 2 ? ' left' : ''}`} style={{ left: `${(x(hover) / W) * 100}%` }}>
            <strong>{when(h.date)} · {h.symbol}</strong>
            <span>{t('This trade')}: <span className={h.net >= 0 ? 'gain' : 'loss'}>{formatMoney(h.net)}</span></span>
            <span>{t('Running total')}: <span className={h.value >= 0 ? 'gain' : 'loss'}>{formatMoney(h.value)}</span></span>
            {h.afterTwo && <span className="loss">{t('After 2 losses that day')}</span>}
          </div>
        )}
      </div>
    </section>
  );
}

// Net P&L by the hour the trade started (the viewer's own time).
export function HourChart({ trades }) {
  const { t } = useI18n();
  const timed = trades.filter((tr) => !tr.timeUnknown);
  const byHour = new Map();
  for (const tr of timed) {
    const h = new Date(tr.date).getHours();
    const row = byHour.get(h) || { hour: h, net: 0, count: 0 };
    row.net += netPnl(tr);
    row.count += 1;
    byHour.set(h, row);
  }
  const rows = [...byHour.values()].sort((a, b) => a.hour - b.hour);
  if (rows.length < 2) return null;
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.net)));
  const label = (h) => new Date(2026, 0, 5, h).toLocaleTimeString(currentLocale(), { hour: 'numeric' });
  return (
    <section className="insight-panel report-chart">
      <h2>{t('Your P&L by hour')}</h2>
      <p className="admin-panel-sub">{t('Net P&L after fees by the hour each trade started, in your local time.')}</p>
      <ul className="report-hours">
        {rows.map((r) => (
          <li key={r.hour} title={t('{hour}: {amount} over {n} trades', { hour: label(r.hour), amount: formatMoney(r.net), n: r.count })}>
            <span className="report-hour-label">{label(r.hour)}</span>
            <span className="report-hour-track">
              <span className={`report-hour-bar ${r.net >= 0 ? 'up' : 'down'}`} style={{ width: `${(Math.abs(r.net) / maxAbs) * 50}%` }} />
            </span>
            <span className={`report-hour-value ${r.net >= 0 ? 'gain' : 'loss'}`}>{formatMoney(r.net)} <small>· {r.count}</small></span>
          </li>
        ))}
      </ul>
    </section>
  );
}
